import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../api";
import {
  DestinationSystem,
  EntityType,
  JobStatus,
  MigrationRunStatus,
  TargetEnvironment,
  type BodyCreateOnboardingImportScopeType,
  type EntityRecordPatch,
  type MyPayEnvironment,
  type PatchWorkPatternRow,
  type WorkPatternRow,
} from "../../../generated/core-api";
// Deep-imported (not the `../../../onboarding` barrel, and not even the safer
// `../../../onboarding/hooks` barrel): mirrors the established precedent in
// `useOnboardingWorkload.ts`'s own barrel-avoidance comment and
// `src/pay-component-import`'s existing import style — going through either
// barrel drags in `./screens`/`./layouts` (top barrel) or the whole hooks
// surface, which has previously broken `vi.mock` partial-mocking for other
// modules that import onboarding hooks transitively.
import { useOnboardingWorkload } from "../../../onboarding/hooks/useOnboardingWorkload";
import { useOnboardingImport } from "../../../onboarding/hooks/useOnboardingImport";
import { useOnboardingValidation } from "../../../onboarding/hooks/useOnboardingValidation";
import { useOnboardingMigrationRun } from "../../../onboarding/hooks/useOnboardingMigrationRun";
import {
  computeStandardValuesMismatchMessage,
  type WorkPatternImportParseFailedRow,
  type WorkPatternImportWarning,
} from "./useWorkPatternImportCsvParser";
import {
  computeHasUnacknowledgedWarnings,
  mergeWarningsByIndex,
  type WorkPatternImportCommitOptions,
  type WorkPatternImportManagerReturn,
  type WorkPatternImportResult,
} from "./useWorkPatternImportInvocation";
import {
  mapRunViewToWorkPatternImportResult,
  mapValidationReportToWorkPatternImportResult,
} from "./workPatternWorkloadsResultMappers";
import { rowsToJsonImportFile } from "../../../onboarding/utils/rowsToJsonImportFile";
import { resolveIfMatchSourceVersion } from "../../../onboarding/utils/resolveIfMatchSourceVersion";
import type { ScopeTypeOption } from "../../types";

/**
 * LADR-039 (#440) Step 8 — the Slice 5 **workloads** REST replacement for
 * `useWorkPatternImportInvocation` (Track 1, `invokeAgent`/Temporal). Exposes
 * the exact same `WorkPatternImportManagerReturn` contract so
 * `WorkPatternImportInput`/`WorkPatternImportTable`/`WorkPatternImportReport`
 * need no changes — Step 9 switches between the two hooks behind
 * `FeatureFlag.EnableWorkloadsCutover` at the screen level; this hook itself
 * does not read the flag.
 *
 * Pipeline (see `.plans/440-feature-ladr-039-portal-cutover.md` Step 8 and
 * `.plans/ladr-039-contract-mapping.md`'s work-patterns vertical for the
 * authoritative shape mapping):
 *
 *   import (multipart file, W1) --> optional PATCH (edited rows, W2) --> validations --> run
 *
 * Unlike Track 1 (which resends the full client-parsed `patterns` array on
 * every validate/commit call), Track 2 uploads the source file **once** and
 * thereafter holds the canonical rows server-side. `useOnboardingValidation`'s
 * `createOnboardingEntityValidation` call carries **no row body at all** — it
 * always validates whatever `canonical_entities` are currently persisted for
 * the workload's `work_patterns` entity record. This makes the "optional
 * PATCH" in the Step 8 description structurally necessary, not cosmetic:
 * after `updatePattern`/`removePattern` edits the client-side `pendingPatterns`
 * array, the next `validate()` call must `PATCH` the entity record with the
 * full edited row list *before* re-validating, or the server would silently
 * re-validate stale (pre-edit) rows.
 *
 * **ETag / If-Match:** OpenAPI still omits a modelled `If-Match` parameter on
 * `patchOnboardingEntityRecord`, but `customCoreInstance` now merges the
 * `ETag` response header onto `EntityRecordView` as `etag` for downstream
 * optimistic concurrency when the generated client gains header support.
 */

const DESTINATION_SYSTEM = DestinationSystem.christmas;
const WORK_PATTERNS_ENTITY_TYPE = EntityType.work_patterns;

/**
 * `PatchWorkPatternRow` (PATCH-only strict variant, `extra="forbid"`) is
 * structurally identical to `WorkPatternRow` (shared with Track 1 CSV
 * parsing, permissive `extra`) today — see the generated docstrings in
 * `src/generated/core-api.ts`. Mapped field-by-field (rather than a bare
 * cast/spread) so the one place the two shapes could diverge in a future API
 * change is this function, not every call site.
 */
function toPatchWorkPatternRow(row: WorkPatternRow): PatchWorkPatternRow {
  return {
    workPatternName: row.workPatternName,
    employeeCode: row.employeeCode,
    workPatternCode: row.workPatternCode,
    cycleDays: row.cycleDays,
    startDate: row.startDate,
    payPublicHolidays: row.payPublicHolidays,
    standardHours: row.standardHours,
    standardDays: row.standardDays,
    dayHours: row.dayHours,
  };
}

/**
 * `BodyCreateOnboardingImportScopeType` only admits `'Employee Group' | null`
 * — narrower than the local `ScopeTypeOption` (`"Employee" | "Employee
 * Group"`), which still carries the legacy `"Employee"` member the module is
 * in the process of removing (see `src/work-patterns/AGENTS.md` — scope is
 * hardcoded to `"Employee Group"` at the call site today). Map explicitly
 * instead of casting so an unexpected `"Employee"` value is omitted from the
 * request rather than silently forwarded as a value the generated type (and
 * likely the API) doesn't accept.
 */
function toApiScopeType(
  scopeType: ScopeTypeOption | null
): BodyCreateOnboardingImportScopeType | undefined {
  return scopeType === "Employee Group" ? scopeType : undefined;
}

/**
 * Orchestrates the work pattern import flow via the Slice 5 workloads REST
 * surface. See the module-level doc comment above for the pipeline shape and
 * the ETag/If-Match residual.
 */
export function useWorkPatternImportWorkloadsManager(): WorkPatternImportManagerReturn {
  const queryClient = useQueryClient();

  // ------------------------------------------------------------------
  // Parse / pending state — same shape as the Track 1 manager, plus the raw
  // `File` (W1: the server, not the client, owns the canonical CSV parse).
  // ------------------------------------------------------------------

  const [pendingPatterns, setPendingPatterns] = useState<WorkPatternRow[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [parseFailedRows, setParseFailedRows] = useState<
    WorkPatternImportParseFailedRow[]
  >([]);
  const [patternCsvRowNumbers, setPatternCsvRowNumbers] = useState<number[]>(
    []
  );
  const [parseWarnings, setParseWarnings] = useState<
    WorkPatternImportWarning[]
  >([]);
  const [companyCode, setCompanyCode] = useState<string | null>(null);
  const [scopeType, setScopeType] = useState<ScopeTypeOption | null>(null);
  const [mypayEnvironment, setMypayEnvironmentState] =
    useState<MyPayEnvironment | null>(null);
  /** Raw CSV text from the uploaded file — used to build the errors CSV (#414). */
  const [rawCsvText, setRawCsvText] = useState<string | null>(null);
  /** Imperative pre-flight guard errors only; stage failures are derived below. */
  const [guardError, setGuardError] = useState<string | null>(null);
  const [validationStale, setValidationStale] = useState(false);
  const [ignoredWarningIndices, setIgnoredWarningIndices] = useState<
    ReadonlySet<number>
  >(() => new Set());
  /**
   * True from a `validate()` call until the import-\>optional-PATCH-\>validate
   * pipeline reaches a terminal state (success or a stage failure). Drives
   * the reactive effect below; unlike Track 1's per-workflow poll flags,
   * one flag covers the whole multi-stage pipeline since only one stage is
   * ever in flight at a time.
   */
  const [isPipelineActive, setIsPipelineActive] = useState(false);

  /** Synchronous ref mirrors — prevent React state-batching races (same rationale as Track 1). */
  const pendingPatternsRef = useRef<WorkPatternRow[]>([]);
  const companyCodeRef = useRef<string | null>(null);
  const scopeTypeRef = useRef<ScopeTypeOption | null>(null);
  const mypayEnvironmentRef = useRef<MyPayEnvironment | null>(null);
  const pendingFileRef = useRef<File | null>(null);
  /** Guards the commit cache-invalidation side effect to fire exactly once. */
  const commitInvalidatedRef = useRef(false);
  /**
   * Gates the async if_match lookup + import kickoff (LADR-039 R-S1-1) so the
   * driver effect cannot fire concurrent create-import calls while awaiting.
   */
  const importKickoffInFlightRef = useRef(false);

  // ------------------------------------------------------------------
  // Composed workloads hooks (Phase 1, Steps 4/5/5b/5c)
  // ------------------------------------------------------------------

  const workload = useOnboardingWorkload({
    companyCode: companyCode ?? undefined,
    destinationSystem: DESTINATION_SYSTEM,
  });
  const importState = useOnboardingImport();
  const validationState = useOnboardingValidation();
  const runState = useOnboardingMigrationRun();

  /**
   * No dedicated `useOnboardingEntityRecordPatch` hook exists yet (see
   * `src/onboarding/hooks/index.ts`), so this is a local, one-off mutation
   * wrapping the generated `patchOnboardingEntityRecord` client directly —
   * matching the "Prefer generated API clients" rule in the root `AGENTS.md`
   * while keeping the PATCH call scoped to this manager until a second
   * caller justifies extracting a shared hook.
   */
  const patchMutation = useMutation({
    mutationFn: async (args: {
      workloadId: string;
      rows: WorkPatternRow[];
    }) => {
      const entityRecordPatch: EntityRecordPatch = {
        canonical_entities: args.rows.map(toPatchWorkPatternRow),
      };
      // ETag is returned only as a response header; CoreAxiosInstance copies
      // it onto the GET payload as `etag` for If-Match on PATCH.
      const current = (await api.getOnboardingEntityRecord(
        args.workloadId,
        WORK_PATTERNS_ENTITY_TYPE
      )) as Awaited<ReturnType<typeof api.getOnboardingEntityRecord>> & {
        etag?: string;
      };
      return api.patchOnboardingEntityRecord(
        args.workloadId,
        WORK_PATTERNS_ENTITY_TYPE,
        entityRecordPatch,
        current.etag
          ? { headers: { "If-Match": current.etag } }
          : undefined
      );
    },
  });

  // ------------------------------------------------------------------
  // Driver effect — advances the import -> optional PATCH -> validate
  // pipeline one stage at a time. Each branch either kicks off the next
  // not-yet-started stage or returns early to wait for the in-flight one to
  // settle; the effect re-runs whenever any stage's status changes.
  //
  // `importState.invoke`/`validationState.invoke`/`patchMutation.mutate` are
  // intentionally omitted from the dependency array: `invoke` is a
  // `useCallback([])`-wrapped stable reference (see `useOnboardingImport.ts`
  // et al.) and `useMutation`'s `mutate` is stable across renders regardless
  // of the inline `mutationFn` closure identity — the same assumption those
  // three hooks' own `invoke` callbacks already rely on.
  // ------------------------------------------------------------------

  useEffect(() => {
    if (!isPipelineActive) return;

    if (workload.isError) {
      setIsPipelineActive(false);
      return;
    }
    const workloadId = workload.data?.workloadId;
    if (!workloadId) {
      // Workload query still resolving (create-or-reuse); wait for it.
      return;
    }

    // Stage 1: import the source file (skipped once already succeeded for
    // this batch — e.g. on a re-validate after an edit).
    if (importState.status !== JobStatus.succeeded) {
      if (importState.status === JobStatus.failed || importState.error) {
        importKickoffInFlightRef.current = false;
        setIsPipelineActive(false);
        return;
      }
      if (!importState.isInvoking && !importState.importId) {
        const sourceFile = pendingFileRef.current;
        if (!sourceFile) {
          setIsPipelineActive(false);
          setGuardError("No file to import. Please choose a CSV file first.");
          return;
        }
        if (importKickoffInFlightRef.current) {
          return;
        }
        importKickoffInFlightRef.current = true;
        // API `WorkPatternSourceImportAdapter` + router allowlist expect a
        // JSON array of WorkPatternRow (portal already parsed the CSV).
        // LADR-039 R-S1-1: re-import must echo source_version as if_match.
        const file = rowsToJsonImportFile(
          pendingPatternsRef.current,
          sourceFile.name
        );
        const companyCode = companyCodeRef.current ?? undefined;
        const scopeType = toApiScopeType(scopeTypeRef.current);
        void (async () => {
          try {
            const ifMatchSourceVersion = await resolveIfMatchSourceVersion(
              workloadId,
              WORK_PATTERNS_ENTITY_TYPE
            );
            importState.invoke({
              workloadId,
              file,
              entityType: WORK_PATTERNS_ENTITY_TYPE,
              companyCode,
              scopeType,
              ifMatchSourceVersion,
            });
          } catch {
            importKickoffInFlightRef.current = false;
            setIsPipelineActive(false);
            setGuardError(
              "Could not check existing import state before uploading. Please try again."
            );
          }
        })();
      }
      return;
    }

    // Stage 2: PATCH the held canonical rows if the client made edits since
    // the last successful import/patch (W2 — never re-CSV for edits).
    if (validationStale) {
      if (patchMutation.isError) {
        setIsPipelineActive(false);
        return;
      }
      if (patchMutation.isSuccess) {
        // The PATCH landed; clear the staleness flag so the *next* effect
        // run (triggered by this state change) falls through to stage 3
        // instead of re-entering this branch and looping forever — a bug
        // caught by this hook's own test suite (`updatePattern`/
        // `removePattern` re-validate cycles) before this fix.
        setValidationStale(false);
        return;
      }
      if (!patchMutation.isPending) {
        patchMutation.mutate({
          workloadId,
          rows: pendingPatternsRef.current,
        });
      }
      return;
    }

    // Stage 3: validate. `destinationDryRun: true` requests the Christmas
    // dry-run in addition to Pacific validation, for parity with Track 1
    // (whose `work_pattern_import` workflow always validated against
    // Christmas). Gated server-side by `ONBOARDING_MIGRATION_RUNS_ENABLED` —
    // Api #1051 shipped this dark (false in Sandbox/Preview/Production until
    // a deliberate cutover) — not re-checked client-side.
    if (validationState.status !== JobStatus.succeeded) {
      if (validationState.status === JobStatus.failed || validationState.error) {
        setIsPipelineActive(false);
        return;
      }
      if (!validationState.isInvoking && !validationState.validationId) {
        const batchMypayEnvironment = mypayEnvironmentRef.current;
        if (!batchMypayEnvironment) {
          setIsPipelineActive(false);
          setGuardError(
            "No MyPay environment selected. Please choose an environment before importing."
          );
          return;
        }
        validationState.invoke({
          workloadId,
          entityType: WORK_PATTERNS_ENTITY_TYPE,
          destinationDryRun: true,
          mypayEnvironment: batchMypayEnvironment,
          targetEnvironment: TargetEnvironment.test,
        });
      }
      return;
    }

    // All stages terminal-succeeded.
    setIsPipelineActive(false);
    setValidationStale(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPipelineActive,
    workload.isError,
    workload.data?.workloadId,
    importState.status,
    importState.isInvoking,
    importState.importId,
    validationStale,
    patchMutation.isPending,
    patchMutation.isSuccess,
    patchMutation.isError,
    validationState.status,
    validationState.isInvoking,
    validationState.validationId,
  ]);

  // Cache invalidation is a side effect (can't run during render) — fired
  // exactly once when the run reaches a successful terminal status.
  useEffect(() => {
    if (
      runState.status === MigrationRunStatus.succeeded &&
      !commitInvalidatedRef.current
    ) {
      commitInvalidatedRef.current = true;
      void queryClient.invalidateQueries({ queryKey: ["work-patterns"] });
    }
  }, [runState.status, queryClient]);

  // ------------------------------------------------------------------
  // Derived result state
  // ------------------------------------------------------------------

  const validationReport = useMemo<WorkPatternImportResult | null>(() => {
    // Blocking Pacific / Christmas dry-run errors mark Validation `failed`
    // while still attaching a report (Api: FAILED if report.is_blocked).
    if (validationState.status === JobStatus.succeeded) {
      return mapValidationReportToWorkPatternImportResult(
        validationState.data?.report,
        pendingPatterns,
        parseFailedRows.length
      );
    }
    if (validationState.status === JobStatus.failed) {
      const report = validationState.data?.report;
      const hasFindings =
        (report?.errors?.length ?? 0) > 0 || (report?.warnings?.length ?? 0) > 0;
      if (!hasFindings) return null;
      return mapValidationReportToWorkPatternImportResult(
        report,
        pendingPatterns,
        parseFailedRows.length
      );
    }
    return null;
  }, [validationState.status, validationState.data, pendingPatterns, parseFailedRows.length]);

  const commitResult = useMemo<WorkPatternImportResult | null>(() => {
    if (!runState.data) return null;
    return mapRunViewToWorkPatternImportResult(
      runState.data,
      pendingPatterns,
      validationState.data?.report
    );
  }, [runState.data, pendingPatterns, validationState.data]);

  // Simplified error surfacing vs Track 1's `classifyInvocationError`/
  // `classifyPollError` (which classify Temporal/agent-invocation-specific
  // HTTP status codes): the workloads REST API has a different error shape,
  // so this derives one generic message per stage instead of reusing those
  // classifiers.
  const managerError = useMemo<string | null>(() => {
    if (guardError) return guardError;
    if (workload.isError) {
      return "Could not create or load the onboarding workload. Please try again.";
    }
    if (importState.error) {
      return `Import failed to start: ${importState.error}`;
    }
    if (importState.status === JobStatus.failed) {
      return (
        importState.data?.outcome_summary ??
        "The file import failed. Please try again."
      );
    }
    if (patchMutation.isError) {
      return "Could not save your edits before re-validating. Please try again.";
    }
    if (validationState.error) {
      return `Validation failed to start: ${validationState.error}`;
    }
    if (validationState.status === JobStatus.failed) {
      const report = validationState.data?.report;
      const hasReportFindings =
        (report?.errors?.length ?? 0) > 0 || (report?.warnings?.length ?? 0) > 0;
      if (!hasReportFindings) {
        return "Validation failed. Please try again.";
      }
    }
    if (runState.error) {
      return `Import run failed to start: ${runState.error}`;
    }
    if (runState.status === MigrationRunStatus.failed) {
      return "The import run failed. Please try again.";
    }
    return null;
  }, [
    guardError,
    workload.isError,
    importState.error,
    importState.status,
    importState.data?.outcome_summary,
    patchMutation.isError,
    validationState.error,
    validationState.status,
    validationState.data?.report,
    runState.error,
    runState.status,
  ]);

  // ------------------------------------------------------------------
  // Public interface
  // ------------------------------------------------------------------

  const setPendingData = (
    patterns: WorkPatternRow[],
    errors: string[] = [],
    warnings: WorkPatternImportWarning[] = [],
    nextCompanyCode: string | null = null,
    nextScopeType: ScopeTypeOption | null = null,
    nextParseFailedRows: WorkPatternImportParseFailedRow[] = [],
    nextPatternCsvRowNumbers: number[] = [],
    nextRawCsvText: string | null = null,
    file?: File,
    // Accepted only to keep this signature identical to Track 1's
    // `WorkPatternImportManagerReturn.setPendingData` (see that interface's
    // doc comment) — Track 1 uses it to recompute the CSV header's Day-column
    // count against `CycleDays` on each `updatePattern` edit; this manager
    // has no such recompute today (not part of the shared public interface,
    // and the server already validates the imported rows server-side).
    _dayColumnCountFromHeader?: number
  ) => {
    pendingPatternsRef.current = patterns;
    companyCodeRef.current = nextCompanyCode;
    scopeTypeRef.current = nextScopeType;
    pendingFileRef.current = file ?? null;
    importKickoffInFlightRef.current = false;
    setPendingPatterns(patterns);
    setParseErrors(errors);
    setParseFailedRows(nextParseFailedRows);
    setPatternCsvRowNumbers(nextPatternCsvRowNumbers);
    setParseWarnings(warnings);
    setCompanyCode(nextCompanyCode);
    setScopeType(nextScopeType);
    setRawCsvText(nextRawCsvText);
    setValidationStale(false);
    setIgnoredWarningIndices(new Set());
    setGuardError(null);
    setIsPipelineActive(false);
    importState.reset();
    validationState.reset();
    runState.reset();
    patchMutation.reset();
    commitInvalidatedRef.current = false;
  };

  const validate = async (): Promise<void> => {
    const patterns = pendingPatternsRef.current;
    if (patterns.length === 0) return;

    if (!companyCodeRef.current) {
      setGuardError(
        "No company code found. Please enter and validate a company code first."
      );
      return;
    }
    if (!scopeTypeRef.current) {
      setGuardError(
        "No scope type selected. Please select a scope type before importing."
      );
      return;
    }
    if (!mypayEnvironmentRef.current) {
      setGuardError(
        "No MyPay environment selected. Please choose an environment before importing."
      );
      return;
    }
    // A file is only required the first time this batch is imported; a
    // re-validate after an edit reuses the already-imported server record
    // (via PATCH) and needs no file.
    if (importState.importId === null && !pendingFileRef.current) {
      setGuardError("No file to import. Please choose a CSV file first.");
      return;
    }

    setGuardError(null);
    setIsPipelineActive(true);
  };

  const commit = async (
    options?: WorkPatternImportCommitOptions
  ): Promise<void> => {
    const patterns = pendingPatternsRef.current;
    const workloadId = workload.data?.workloadId;
    const batchMypayEnvironment = mypayEnvironmentRef.current;

    const apiWarnings = (commitResult ?? validationReport)?.warnings ?? [];
    const mergedWarnings = mergeWarningsByIndex(parseWarnings, apiWarnings);
    let effectiveIgnored: ReadonlySet<number> = ignoredWarningIndices;
    if (options?.overrideUnacknowledgedWarnings) {
      const nextIgnored = new Set(ignoredWarningIndices);
      for (const [index, messages] of mergedWarnings) {
        if ((messages?.length ?? 0) > 0) {
          nextIgnored.add(index);
        }
      }
      setIgnoredWarningIndices(nextIgnored);
      effectiveIgnored = nextIgnored;
    }
    const unacknowledgedWarnings = computeHasUnacknowledgedWarnings(
      mergedWarnings,
      effectiveIgnored
    );

    if (
      patterns.length === 0 ||
      !workloadId ||
      !batchMypayEnvironment ||
      validationStale ||
      validationState.status !== JobStatus.succeeded ||
      unacknowledgedWarnings
    ) {
      return;
    }

    setGuardError(null);
    commitInvalidatedRef.current = false;
    runState.invoke({
      workloadId,
      targetEnvironment: TargetEnvironment.test,
      entityTypes: [WORK_PATTERNS_ENTITY_TYPE],
      mypayEnvironment: batchMypayEnvironment,
    });
  };

  const clear = () => {
    pendingPatternsRef.current = [];
    companyCodeRef.current = null;
    scopeTypeRef.current = null;
    pendingFileRef.current = null;
    importKickoffInFlightRef.current = false;
    setPendingPatterns([]);
    setParseErrors([]);
    setParseFailedRows([]);
    setPatternCsvRowNumbers([]);
    setParseWarnings([]);
    setCompanyCode(null);
    setScopeType(null);
    setRawCsvText(null);
    setGuardError(null);
    setValidationStale(false);
    setIgnoredWarningIndices(new Set());
    setIsPipelineActive(false);
    importState.reset();
    validationState.reset();
    runState.reset();
    patchMutation.reset();
    commitInvalidatedRef.current = false;
  };

  const removePattern = (index: number) => {
    const next = pendingPatternsRef.current.filter((_, i) => i !== index);
    pendingPatternsRef.current = next;
    setPendingPatterns(next);
    setPatternCsvRowNumbers((prev) => prev.filter((_, i) => i !== index));
    setParseWarnings((prev) =>
      prev
        .filter((warning) => warning.patternIndex !== index)
        .map((warning) =>
          warning.patternIndex > index
            ? { ...warning, patternIndex: warning.patternIndex - 1 }
            : warning
        )
    );
    setIgnoredWarningIndices((prev) => {
      const nextIgnored = new Set<number>();
      for (const ignoredIndex of prev) {
        if (ignoredIndex === index) continue;
        nextIgnored.add(
          ignoredIndex > index ? ignoredIndex - 1 : ignoredIndex
        );
      }
      return nextIgnored;
    });
    // Divergence from Track 1 (which does not set `validationStale` here):
    // Track 2's commit/run act on the server-held canonical snapshot, not a
    // client-resent array, so removing a row must also force a PATCH +
    // re-validate cycle before the next commit — otherwise the stale
    // (still-includes-the-removed-row) server snapshot would be pushed.
    setValidationStale(true);
    validationState.reset();
    runState.reset();
    patchMutation.reset();
    commitInvalidatedRef.current = false;
  };

  const updatePattern = (index: number, next: WorkPatternRow) => {
    const updated = pendingPatternsRef.current.map((p, i) =>
      i === index ? next : p
    );
    pendingPatternsRef.current = updated;
    setPendingPatterns(updated);
    setParseWarnings((prev) => {
      const existing = prev.find((w) => w.patternIndex === index);
      const without = prev.filter((w) => w.patternIndex !== index);
      const message = computeStandardValuesMismatchMessage(next);
      if (!message) {
        return without;
      }
      const csvRowNumber =
        existing?.rowNumber ?? patternCsvRowNumbers[index] ?? index + 2;
      return [
        ...without,
        {
          rowNumber: csvRowNumber,
          workPatternName: next.workPatternName,
          patternIndex: index,
          message,
        },
      ];
    });
    setIgnoredWarningIndices((prev) => {
      if (!prev.has(index)) {
        return prev;
      }
      const nextIgnored = new Set(prev);
      nextIgnored.delete(index);
      return nextIgnored;
    });
    setValidationStale(true);
    // A second edit-then-revalidate cycle must re-enter the PATCH stage even
    // though `patchMutation.isSuccess` is still (stale-)true from the prior
    // edit's patch — resetting here, and in `removePattern` above, keeps the
    // driver effect's `!patchMutation.isPending && !patchMutation.isSuccess`
    // gate accurate for this fresh edit.
    validationState.reset();
    runState.reset();
    patchMutation.reset();
    commitInvalidatedRef.current = false;
  };

  const ignoreWarning = (index: number) => {
    setIgnoredWarningIndices((prev) => new Set(prev).add(index));
  };

  const setMyPayEnvironment = (environment: MyPayEnvironment | null) => {
    mypayEnvironmentRef.current = environment;
    setMypayEnvironmentState(environment);
  };

  // ------------------------------------------------------------------
  // Derived state
  // ------------------------------------------------------------------

  // The whole import->PATCH->validate pipeline is a single flag, so
  // "validating" is just "pipeline in flight" — there is no separate
  // per-stage flag to OR together the way Track 1 ORs mutation-pending with
  // poll-in-progress.
  const isValidating = isPipelineActive;
  const isCommitting = runState.isInvoking || runState.isPolling;

  const warningsByPatternIndex = useMemo(() => {
    const apiWarnings = (commitResult ?? validationReport)?.warnings ?? [];
    return mergeWarningsByIndex(parseWarnings, apiWarnings);
  }, [parseWarnings, commitResult, validationReport]);

  const hasUnacknowledgedWarnings = useMemo(
    () =>
      computeHasUnacknowledgedWarnings(
        warningsByPatternIndex,
        ignoredWarningIndices
      ),
    [warningsByPatternIndex, ignoredWarningIndices]
  );

  /**
   * Simplified analogue of Track 1's `computeValidationEndedWithoutReport`:
   * that helper is tailored to `InvocationStatusEnum` specifics (a
   * `TERMINATED` status distinct from `FAILED`, and a client-side poll
   * attempt cap/timeout). `useOnboardingJobPoll` (Track 2) exposes neither a
   * `TERMINATED`-equivalent nor an attempt cap — an inherited limitation,
   * not one introduced here — so this checks the narrower set of terminal
   * failure signals Track 2 actually has.
   */
  const validationEndedWithoutReport = useMemo(() => {
    if (pendingPatterns.length === 0) return false;
    if (validationReport !== null) return false;
    if (isPipelineActive) return false;
    return (
      guardError !== null ||
      workload.isError ||
      importState.status === JobStatus.failed ||
      patchMutation.isError ||
      validationState.status === JobStatus.failed
    );
  }, [
    pendingPatterns.length,
    validationReport,
    isPipelineActive,
    guardError,
    workload.isError,
    importState.status,
    patchMutation.isError,
    validationState.status,
  ]);

  const hasUncommittedImportProgress = useMemo(() => {
    if (commitResult !== null) {
      return false;
    }
    return (
      pendingPatterns.length > 0 ||
      parseErrors.length > 0 ||
      parseFailedRows.length > 0 ||
      validationReport !== null ||
      isValidating ||
      isCommitting
    );
  }, [
    commitResult,
    pendingPatterns.length,
    parseErrors.length,
    parseFailedRows.length,
    validationReport,
    isValidating,
    isCommitting,
  ]);

  return {
    pendingPatterns,
    parseErrors,
    parseFailedRows,
    patternCsvRowNumbers,
    validationEndedWithoutReport,
    parseWarnings,
    warningsByPatternIndex,
    hasParseWarnings: warningsByPatternIndex.size > 0,
    companyCode,
    scopeType,
    mypayEnvironment,
    rawCsvText,
    validationReport,
    commitResult,
    validationStale,
    ignoredWarningIndices,
    hasUnacknowledgedWarnings,
    managerError,
    isValidating,
    isCommitting,
    hasData: pendingPatterns.length > 0,
    hasParseErrors: parseErrors.length > 0,
    hasUncommittedImportProgress,
    setPendingData,
    validate,
    commit,
    clear,
    removePattern,
    updatePattern,
    ignoreWarning,
    setMyPayEnvironment,
  };
}
